import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouteMatch, Link } from 'react-router-dom'
import { useModal } from '@ebay/nice-modal-react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { Icon } from '@iconify/react'
import { useQueryParams } from 'use-query-params'

import {
  Modal,
  useUserDetails,
  Button,
  Skeleton,
  useShowMessage,
  useAssignment,
  useGetWordCount,
  useGetEditorAppContent,
} from '@pepper/ui'
import { checkAuthorization } from '@pepper/utils/src/api/functions/authHelpers'
import {
  getPlagiarismPillClassV2,
  getAuditPropertyKeys,
  getAdherencePillClass,
  getReadabilityScorePillClass,
  isAuditCompleted,
} from '@pepper/utils/src/function/audits'
import { isWordCountPercentOutsideRange } from '@pepper/utils/src/function/isWordCountPercentOutsideRange'
import { ContentAuditReport } from '@pepper/utils/src/types/content_audit'
import { EditorApps, StatusTypes } from '@pepper/utils/src/types/editor'
import { MIN_PLAGIARISM_AUDIT_WORD_COUNT } from '@pepper/utils/src/constant/enum/plagiarism'
import { useGetMarketplaceAuditStatus } from '@pepper/docs/src/hooks/api/content-audit/useGetMarketplaceAuditStatus'
import { useCreateAudit } from '@pepper/docs/src/hooks/api/content-audit/useCreateAudit'

import styles from '../_.module.css'

const PLAGIARISM_PERCENT_LIMIT = 30
const REFETCH_INTERVAL = 2000

type Props = {
  assignmentId: string
  metricsArray: string[]
  handleSubmit: (type: ButtonActionKeys | null) => void
  onCancel: () => void
}

function AuditModalV2({
  assignmentId,
  metricsArray,
  handleSubmit,
  onCancel,
}: Props) {
  const queryClient = useQueryClient()
  const isAuditCreated = useRef(false)
  const { visible: isModalVisible, remove: removeModal } = useModal()
  const match = useRouteMatch('/document/:assignmentId')
  const isEditorPage = match !== null
  const showMessage = useShowMessage()
  const authorization = checkAuthorization()?.data
  const { userDetails } = useUserDetails(authorization?.userId)
  const userRole = userDetails?.role
  const [_, setQueryParams] = useQueryParams({})

  const [auditId, setAuditId] = useState<string>()
  const auditStatus = useGetMarketplaceAuditStatus({
    data: { itemId: assignmentId },
  })
  const { data: assignmentDetails, isLoading: isDetailsLoading } =
    useAssignment({ assignmentId })
  const {
    mutateAsync: createAudit,
    isLoading: isAuditLoading,
    isSuccess: isAuditCallSuccess,
  } = useCreateAudit()
  const auditAppContent = useGetEditorAppContent({
    data: {
      app: EditorApps.CONTENT_AUDIT,
      editorType: 'PEPPER_DOC',
      itemId: assignmentId,
      metadata: { id: auditId },
    },
    options: {
      enabled: Boolean(auditId),
      refetchInterval: response => {
        const data = response as ContentAuditReport | undefined
        if (!data) return false
        return isAuditCompleted(data) ? false : REFETCH_INTERVAL
      },
    },
  })
  const auditData = auditAppContent.data as ContentAuditReport | undefined
  const { data: wordCount, isLoading: isWordCountLoading } = useGetWordCount({
    assignmentId,
  })

  const [auditsKey, auditLimitKey] = useMemo(() => {
    return getAuditPropertyKeys(auditStatus.data, userRole as CreatorRole)
  }, [auditStatus.data, userRole])

  const auditAttemptsLeft = useMemo(() => {
    if (!auditStatus.data) return null
    return auditStatus.data[auditLimitKey] - auditStatus.data[auditsKey].length
  }, [auditLimitKey, auditStatus.data, auditsKey])

  const [auditRunCount, setAuditRunCount] = useState(0)

  const assignmentWordCount = Number(assignmentDetails?.client_word_count)
  const wordCountError = isWordCountPercentOutsideRange(
    assignmentWordCount,
    wordCount
  )
  const readabilityScore = auditData?.readability?.result?.score
  const readabilityError =
    getReadabilityScorePillClass(readabilityScore) !== 'Gray'

  const handleCreateAudit = useCallback(async () => {
    if (!auditStatus.data) return
    if (wordCount < MIN_PLAGIARISM_AUDIT_WORD_COUNT) return

    try {
      const maxTries = auditStatus.data[auditLimitKey]
      const existingTries = auditStatus.data[auditsKey]?.length
      if (
        auditRunCount < 1 &&
        (userRole === 'editor' || existingTries < maxTries)
      ) {
        isAuditCreated.current = true
        try {
          const payload = {
            get_readability_score: true, // readability is always checked
            get_fact_check_report: metricsArray?.includes('fact_check'),
            get_outline_report: false,
            get_dos_and_donts_report: metricsArray?.includes('dos_and_donts'),
            get_plagiarism_score: metricsArray?.includes('plagiarism'),
          }
          const response = await createAudit({
            itemId: assignmentId,
            platform: 'creators',
            payload,
          })
          setAuditId(response.audit_id)
        } catch (error) {
          showMessage({ type: 'error', message: error.message })
        }
        queryClient.invalidateQueries(['editor', 'apps'])
        await auditStatus.refetch()
        if (auditRunCount === maxTries - 1) {
          onCancel()
        }
        setAuditRunCount(count => count + 1)
      }
    } catch (error) {
      showMessage({
        type: 'error',
        message: 'Failed to run audit for the assignment',
      })
    }
  }, [
    auditStatus,
    auditLimitKey,
    auditsKey,
    auditRunCount,
    userRole,
    assignmentId,
    queryClient,
    wordCount,
    showMessage,
    onCancel,
    createAudit,
    metricsArray,
  ])

  // Run content audit on opening the modal
  useEffect(() => {
    if (isAuditCreated.current) return
    handleCreateAudit()
  }, [handleCreateAudit])

  const modalForEmptyContent = useMemo(() => {
    return (
      <Modal
        wrapClassName={clsx(styles.auditModal, styles.emptyModal)}
        title={null}
        footer={null}
        visible={isModalVisible}
        closeIcon={
          <div role="none" onClick={() => removeModal()}>
            <Icon icon="lucide:x" />
          </div>
        }
      >
        <div className={styles.modalTitle}>Empty assignment</div>
        <div className={styles.emptyModalWrapper}>
          <div className={styles.confirmationText}>
            The assignment you are trying to submit does not have enough words.
          </div>
          <div
            className={clsx(styles.footerButtonGroup, styles.footerGroupLeft)}
          >
            <Button
              type="primary"
              onClick={() => {
                onCancel()
                removeModal()
              }}
              className={styles.cancelModalButton}
            >
              {isEditorPage ? (
                'Go back and edit'
              ) : (
                <Link
                  to={`/document/${assignmentId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Go to editor
                </Link>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    )
  }, [isModalVisible, isEditorPage, assignmentId, onCancel, removeModal])

  const modalForAuditSuccess = useMemo(() => {
    return (
      <Modal
        wrapClassName={clsx(styles.auditModal, styles.auditModalV2)}
        title={null}
        footer={null}
        visible={isModalVisible}
      >
        <div className={styles.modalTitle}>Content audit report</div>
        <div className={styles.modalBodyContainer}>
          <div className={styles.noIssuesText}>
            <div className={styles.checkIconWrapper}>
              <Icon
                icon="lucide:circle-check-big"
                color="var(--color-green-500)"
              />
            </div>
            No issues detected! Awesome work!
          </div>
          <div className={styles.confirmationText}>
            Are you sure you want to submit the assignment for approval? Once
            shared, you will not be able to make edits to your latest
            submission.
          </div>
        </div>
        <div
          className={clsx(
            styles.footerButtonGroup,
            styles.applyBorder,
            styles.successModalBtnGroup
          )}
        >
          <Button
            onClick={() => {
              onCancel()
              removeModal()
            }}
            className={styles.submitWithoutViewing}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => {
              handleSubmit('audit')
              removeModal()
            }}
            className={styles.cancelModalButton}
          >
            Submit
          </Button>
        </div>
      </Modal>
    )
  }, [handleSubmit, isModalVisible, onCancel, removeModal])

  const plagiarismPercent = auditData?.plagiarism?.result?.overall_score
  const isPlagiarismPercentAcceptable =
    getPlagiarismPillClassV2(Number(plagiarismPercent)) === 'Gray'

  const adherenceCount =
    Number(
      auditData?.fact_check?.result?.filter(
        issue => issue.accuracy !== 'accurate'
      ).length ?? 0
    ) +
    (auditData?.outline?.result?.length ?? 0) +
    wordCountError +
    (readabilityError ? 1 : 0) +
    (auditData?.grammar_whitespace_checker?.result?.length ?? 0) +
    (auditData?.grammar_hindi_checker?.result?.length ?? 0)

  const plagiarismCount =
    auditData?.plagiarism?.result?.results.filter(issue => issue.score > 0)
      .length ?? 0
  const adherenceSuccess = adherenceCount === 0
  const plagiarismSuccess = (plagiarismPercent ?? 0) <= PLAGIARISM_PERCENT_LIMIT

  const isAnyDataInLoadingState = useMemo(() => {
    return (
      !auditData ||
      isAuditLoading ||
      !isAuditCompleted(auditData) ||
      isWordCountLoading ||
      isDetailsLoading
    )
  }, [auditData, isAuditLoading, isWordCountLoading, isDetailsLoading])

  // no issue detected
  const isAuditSuccess =
    !isAnyDataInLoadingState && adherenceSuccess && plagiarismSuccess

  const renderPlagiarismScore = () => {
    if (wordCount < MIN_PLAGIARISM_AUDIT_WORD_COUNT) return null

    if (auditData?.plagiarism?.status === StatusTypes.IN_PROGRESS) {
      return (
        <Skeleton
          active={true}
          loading={true}
          paragraph={false}
          className={styles.infoValSkeleton}
        />
      )
    }

    return (
      <span
        className={clsx(
          styles.pill,
          styles[`pill${getPlagiarismPillClassV2(Number(plagiarismPercent))}`],
          styles.infoValue
        )}
      >
        {!plagiarismCount ? ' - ' : Number(plagiarismCount)}
      </span>
    )
  }

  const renderAdherenceScore = () => {
    if (auditData && !isAuditCompleted(auditData)) {
      return (
        <Skeleton
          active={true}
          loading={true}
          paragraph={false}
          className={styles.infoValSkeleton}
        />
      )
    }

    return (
      <span
        className={clsx(
          styles.pill,
          styles[`pill${getAdherencePillClass(adherenceCount)}`],
          styles.infoValue
        )}
      >
        {!adherenceCount ? ' - ' : adherenceCount}
      </span>
    )
  }

  if (!isWordCountLoading && wordCount < MIN_PLAGIARISM_AUDIT_WORD_COUNT) {
    return modalForEmptyContent
  }

  if (isAuditSuccess) {
    return modalForAuditSuccess
  }

  const modalHeader = isAnyDataInLoadingState
    ? 'Review in progress'
    : 'Content Review'

  const displaySubmitButton =
    userRole === 'writer' ? auditAttemptsLeft === 0 : true

  return (
    <Modal
      wrapClassName={clsx(styles.auditModal, styles.auditModalV2)}
      title={null}
      footer={null}
      visible={isModalVisible}
      closeIcon={
        <div role="none" onClick={() => removeModal()}>
          <Icon icon="lucide:x" />
        </div>
      }
      closable={isAuditCallSuccess}
    >
      <div className={styles.modalTitle}>{modalHeader}</div>
      <div className={styles.modalBodyContainer}>
        <div className={styles.analyzingText}>
          {isAnyDataInLoadingState ? (
            <p>
              We&apos;re analyzing your content to provide the most accurate
              feedback. This may take some time, but great reviews are worth the
              wait!
            </p>
          ) : (
            <div className={styles.warningText}>
              <div className={styles.alertIcon}>
                <Icon icon="lucide:triangle-alert" />
              </div>
              We&apos;ve identified issues in your assignment. Please resolve
              them before submitting to avoid rework.
            </div>
          )}
        </div>
        <div
          className={clsx(styles.infoCard, {
            [styles.loadingInfoCard]: isAnyDataInLoadingState,
          })}
        >
          <div className={styles.infoHeader}>
            <div className={styles.infoIconWrap}>
              <Icon icon="lucide:list-checks" height={16} width={16} />
            </div>
            <div className={styles.auditField}>
              <span>Adherence</span>
              <div className={styles.infoValueContainer}>
                <span className={styles.issueDetected}>Issues detected:</span>
                <Skeleton
                  active={true}
                  loading={isAnyDataInLoadingState}
                  paragraph={false}
                  className={styles.infoValSkeleton}
                >
                  {renderAdherenceScore()}
                </Skeleton>
              </div>
            </div>
          </div>
        </div>

        {(!isPlagiarismPercentAcceptable || !plagiarismPercent) &&
        metricsArray?.includes('plagiarism') ? (
          <div
            className={clsx(styles.infoCard, {
              [styles.loadingInfoCard]: isAnyDataInLoadingState,
            })}
          >
            <div className={styles.infoHeader}>
              <div className={styles.infoIconWrap}>
                <Icon icon="lucide:badge-check" height={16} width={16} />
              </div>
              <div className={styles.auditField}>
                <span>Proofing</span>
                <span className={styles.infoValueContainer}>
                  <span className={styles.issueDetected}>Issues detected:</span>
                  <Skeleton
                    active={true}
                    loading={isAnyDataInLoadingState}
                    paragraph={false}
                    className={styles.infoValSkeleton}
                  >
                    {renderPlagiarismScore()}
                  </Skeleton>
                </span>
              </div>
            </div>
          </div>
        ) : null}

        <div className={styles.footerButtonGroup}>
          {!isAnyDataInLoadingState && displaySubmitButton && (
            <Button
              onClick={() => {
                handleSubmit('audit')
                removeModal()
              }}
              className={styles.submitWithoutViewing}
            >
              Skip & Submit
            </Button>
          )}
          {!isAnyDataInLoadingState && (
            <Button
              type="primary"
              onClick={() => {
                if (isEditorPage) {
                  setQueryParams({
                    widget: 'AI_REVIEW',
                  })
                }
                onCancel()
                removeModal()
              }}
              className={styles.cancelModalButton}
            >
              {isEditorPage ? (
                'View issues'
              ) : (
                <Link
                  to={`/document/${assignmentId}?widget=AI_REVIEW`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View issues in doc
                </Link>
              )}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default AuditModalV2
